import {
  ActivityToolThumbnail,
  hasActivityToolThumbnail,
} from "@/components/mentor-workflows/action/tools/tool-thumbnail";
import type { ChatRenderContext } from "@/components/shared/chat-message-types";
import { ContentRemovedBadge } from "@/components/shared/content-removed-badge";
import { TakedownActions } from "@/components/shared/takedown-actions";
import { TeenSentActivityEntry } from "@/components/conversations/teen-sent-activity-entry";
import { getActivityConfig, isActivityType } from "@/lib/activities/registry";
import { ACTIVITY_QUERY_KEY } from "@/lib/api/activities";
import { useActivityMessage } from "@/lib/hooks/conversations/use-activity-message";
import { useQueryClient } from "@tanstack/react-query";
import { PlayCircle, Sparkles, XCircle } from "lucide-react";
import type { ReactNode } from "react";

interface ActivityMessageRendererProps {
  message: {
    text: string;
    customType: string;
    data?: string;
  };
  context?: ChatRenderContext;
}

export function ActivityMessageRenderer({
  message,
  context,
}: ActivityMessageRendererProps) {
  const {
    activityData,
    activity,
    isError,
    isPending,
    entityId,
    isEnabled,
    teenName,
    mentorName,
    mentorAvatarUrl,
    teenAvatarUrl,
    openInHub,
  } = useActivityMessage(message, { context });
  const queryClient = useQueryClient();

  if (!activityData?.entity_id || !isEnabled) {
    return <ActivityMessageFallback text={message.text} />;
  }

  const fallbackText = message.text || "Activity update";

  if (isPending) {
    return <ActivityMessageFallback text={fallbackText} />;
  }

  if (isError) {
    return (
      <ActivityMessageFallback text="This activity is no longer available" />
    );
  }

  if (activity?.takedown) {
    return (
      <ContentRemovedBadge body="This content was removed for a safety policy violation. Our team has been notified.">
        <TakedownActions
          alertId={activity.takedownAlertId ?? undefined}
          onReversed={() =>
            queryClient.invalidateQueries({
              queryKey: [ACTIVITY_QUERY_KEY, entityId],
            })
          }
        />
      </ContentRemovedBadge>
    );
  }

  const module = getActivityConfig(activityData.activity_type);
  const messageStatus = activityData.status;
  const label =
    module?.title ?? activity?.activityDefinition?.label ?? "Activity";
  const isAdminSurface = context?.surface === "admin";
  const activityToolId = hasActivityToolThumbnail(activityData.activity_type)
    ? activityData.activity_type
    : null;

  if (messageStatus === "sent") {
    if (
      activity &&
      activity.status !== "sent" &&
      activity.status !== "in_progress"
    ) {
      return <ActivityMessageFallback text={fallbackText} />;
    }

    if (activity?.initiator === "teen") {
      if (
        isAdminSurface ||
        !module?.ResponderDialog ||
        !isActivityType(activityData.activity_type)
      ) {
        return (
          <ActivityMessageFallback
            text={`${teenName} sent ${label.toLowerCase()}`}
          />
        );
      }
      return (
        <TeenSentActivityEntry
          ResponderDialog={module.ResponderDialog}
          activityType={activityData.activity_type}
          entityId={entityId}
          teenName={teenName}
          label={label}
          teenAvatarUrl={teenAvatarUrl}
          mentorAvatarUrl={mentorAvatarUrl}
        />
      );
    }

    if (activity?.isExpired) {
      return (
        <div className="flex items-center justify-center py-3 opacity-40">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-zinc-400" />
            <span className="text-sm text-zinc-400">Activity expired</span>
          </div>
        </div>
      );
    }

    const isInProgress = activity?.status === "in_progress";

    return (
      <div className="flex items-center justify-center py-3">
        <div className="flex items-center gap-2">
          {isInProgress ? (
            <PlayCircle className="h-4 w-4 text-zinc-500" />
          ) : activityToolId ? (
            <ActivityToolThumbnail id={activityToolId} />
          ) : (
            <Sparkles className="h-4 w-4 text-indigo-500" />
          )}
          {isInProgress ? (
            <span className="text-sm text-zinc-600">
              {getInProgressCaption({
                teenName,
                label,
              })}
            </span>
          ) : (
            <SentCaption
              fallbackText={fallbackText}
              isAdminSurface={isAdminSurface}
              label={label}
            />
          )}
        </div>
      </div>
    );
  }

  if (messageStatus === "in_progress") {
    return (
      <ActivityMessageFallback
        text={getInProgressCaption({
          teenName,
          label,
        })}
      />
    );
  }

  if (messageStatus === "completed") {
    const result = activity?.result || activityData.result;

    return (
      <div className="flex flex-col items-center py-3">
        {module ? (
          <module.ResultCard
            participants={{
              teen: { name: teenName, avatarUrl: teenAvatarUrl },
              mentor: { name: mentorName, avatarUrl: mentorAvatarUrl },
            }}
            result={result ?? {}}
            config={activity?.config ?? {}}
            onClick={openInHub}
          />
        ) : (
          <div className="rounded-lg bg-zinc-50 px-4 py-3">
            <span className="text-sm text-zinc-500">Activity completed</span>
          </div>
        )}
      </div>
    );
  }

  if (messageStatus === "declined") {
    return (
      <div className="flex items-center justify-center py-3 opacity-60">
        <div className="flex items-center gap-2">
          <XCircle className="h-4 w-4 text-zinc-400" />
          <span className="text-sm text-zinc-400">
            {getDeclinedCaption({
              teenName,
              label,
            })}
          </span>
        </div>
      </div>
    );
  }

  return <ActivityMessageFallback text={fallbackText} />;
}

function ActivityMessageFallback({ text }: { text: string }) {
  return (
    <div className="flex items-center justify-center py-3">
      <span className="text-sm text-zinc-600">{text}</span>
    </div>
  );
}

function SentCaption({
  fallbackText,
  isAdminSurface,
  label,
}: {
  fallbackText: string;
  isAdminSurface: boolean;
  label: string;
}): ReactNode {
  if (isAdminSurface) {
    return <span className="text-sm text-zinc-600">{fallbackText}</span>;
  }

  return (
    <span className="text-sm">
      <span className="text-zinc-500">You sent </span>
      <span className="font-medium text-zinc-800">{label}</span>
    </span>
  );
}

function getInProgressCaption({
  teenName,
  label,
}: {
  teenName: string;
  label: string;
}) {
  return `${teenName} is working on ${label}`;
}

function getDeclinedCaption({
  teenName,
  label,
}: {
  teenName: string;
  label: string;
}) {
  return `${teenName} declined ${label}`;
}
